import {jsonrepair} from 'jsonrepair';
import deepMerge from 'deepmerge';

export type TKeys<T extends object> = keyof T extends string ? keyof T : never;

export function clone<T extends {}>(target: T, isRecursive = true): T {
	if (!isRecursive) {
		return {...target};
	}
	return deepMerge<T>({}, target);
};

export function deepMergeAssign<
	TTarget extends object,
	TSources extends readonly object[]
>(
	target: TTarget,
	sources: TSources,
	options?: deepMerge.Options
): TTarget & UnionToIntersection<TSources[number]> {
	const merged = deepMerge.all(
		[target, ...sources],
		Object.assign({
			isMergeableObject: (x: any) => (
				x &&
				x.constructor &&
				((x instanceof Array) || (x.constructor === Object))
			)
		}, options)
	);
	return (Object.assign(target, merged) as any);
};
type UnionToIntersection<U> =
	(U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never;

export interface TClass<T, A extends any[]> extends Function {
	new(...args: A): T;
};
export type PropertyType<TObj, TProp extends keyof TObj> = TObj[TProp];
export const flatten = (() => {
	const flatten = (
		objIn: {},
		objOut: Record<any, any>,
		path: string
	) => {
		Object.entries(objIn).forEach(function (x) {
			const k = (path === '') ? x[0] : path + x[0];
			if (x[1] instanceof Object) {
				flatten(x[1], objOut, k + '.');
			} else {
				objOut[k] = x[1];
			}
		});
		return (objOut);
	};
	return ((obj: {}) => {
		return (flatten(obj, {}, ''));
	});
})();

export function get(
	root: Record<any, any>,
	path: string[]
) {
	let obj: Record<any, any> | unknown = root;
	let p: any = null;
	for (const key of path) {
		if ((obj === null) || (typeof obj !== 'object')) {
			return (undefined);
		}
		p = obj;
		obj = (<any>obj)[key];
	}
	if (typeof obj === 'function') {
		const _c = obj;
		obj = function (this: unknown, ...x: any[]) {
			const self = this ? this : p;
			const res = _c.call(self, ...x);
			return (res);
		};
	}
	return (obj);
};

export function getPathFromAtoB(
	a: object,
	b: object
) {
	const f2 = (
		a: object,
		b: object,
		currentPath: string[]
	): string[] | null => {
		const toInspectDeepen = [];
		let found: string[] | null = null;
		for (const entries of Object.entries(a)) {
			if (entries[1] === b) {
				found = [...currentPath, entries[0]];
			} else if (typeof entries[1] === 'object') {
				toInspectDeepen.push([[...currentPath, entries[0]], entries[1]]);
			}
		}
		if (found) {
			return (found);
		}
		for (const toInspect of toInspectDeepen) {
			const result = f2(toInspect[1], b, toInspect[0]);
			if (result) {
				return (result);
			}
		}
		return (null);
	};
	return (f2(a, b, []));
}

export function getParentClassFromClass(
	_class: TClass<unknown, any>
) {
	return _class.prototype.__proto__?.constructor || null;
};

export function getInheritanceChainFromClass(
	_class: TClass<unknown, any>
) {
	const classes = [];
	while (_class) {
		classes.push(_class);
		if (_class === Object) {
			break;
		}
		_class = getParentClassFromClass(_class);
	}
	return (classes);
};

export function getInheritanceChainFromInstance(
	instance: { constructor: TClass<unknown, any> }
) {
	return (getInheritanceChainFromClass(instance.constructor));
};

export function setCompareVersion<T>(
	_new: Set<T>,
	_old: Set<T>
) {
	const diff = {
		added: <T[]>[],
		removed: <T[]>[],
		kept: <T[]>[]
	};
	for (const n of _new) {
		if (_old.has(n)) {
			diff.kept.push(n);
		} else {
			diff.added.push(n);
		}
	}
	for (const o of _old) {
		if (!_new.has(o)) {
			diff.removed.push(o);
		}
	}
	return (diff);
};

/**
 * Compare two objects recursively and return a diff.
 **/
export function objectCompare(
	_new: object,
	_old: object
) {
	type TDiffItem<TValueField extends string> = (
		{ readonly path: string } &
		{ readonly [K in TValueField]: any }
		);
	const createDiff = () => {
		return {
			added: <TDiffItem<'valueNew'>[]>[],
			removed: <TDiffItem<'valueOld'>[]>[],
			updated: <TDiffItem<'valueOld' | 'valueNew'>[]>[],
			unchanged: </** Property path. **/string[]>[],
		};
	};
	const rootDiff = createDiff();
	type TValue = any;
	const isMergeableObject = (x: unknown): x is object => (
		!!x &&
		typeof x === 'object' &&
		(
			(x instanceof Array) ||
			((x as any).constructor === Object)
		)
	);
	const isEqualScalar = (a: unknown, b: unknown) => Object.is(a, b);
	const joinPath = (parent: string, key: string) => (parent ? `${parent}.${key}` : key);
	/** Prevent infinite loops on cyclic graphs. */
	const seen = new WeakMap<object, WeakSet<object>>();
	const markSeen = (a: object, b: object) => {
		let set = seen.get(a);
		if (!set) {
			set = new WeakSet<object>();
			seen.set(a, set);
		}
		if (set.has(b)) {
			return true;
		}
		set.add(b);
		return false;
	};
	const walk = (valueNew: TValue, valueOld: TValue, path: string): boolean => {
		const hasNew = valueNew !== undefined;
		const hasOld = valueOld !== undefined;
		if (hasNew && !hasOld) {
			rootDiff.added.push({path, valueNew});
			return true;
		} else if (!hasNew && hasOld) {
			rootDiff.removed.push({path, valueOld});
			return true;
		} else if (!hasNew && !hasOld) {
			// Typically unreachable, but keep coherent behavior.
			if (path) {
				rootDiff.unchanged.push(path);
			}
			return false;
		}
		// Both defined
		const nObj = isMergeableObject(valueNew);
		const oObj = isMergeableObject(valueOld);
		// Different "mergeable-ness" OR different constructors => scalar compare
		if (
			(nObj !== oObj) ||
			(nObj && oObj && (valueNew as any).constructor !== (valueOld as any).constructor)
		) {
			if (isEqualScalar(valueNew, valueOld)) {
				if (path) {
					rootDiff.unchanged.push(path);
				}
				return false;
			}
			rootDiff.updated.push({path, valueOld, valueNew});
			return true;
		}
		// Both are not mergeable objects => scalar compare
		if (!nObj && !oObj) {
			if (isEqualScalar(valueNew, valueOld)) {
				if (path) {
					rootDiff.unchanged.push(path);
				}
				return false;
			}
			rootDiff.updated.push({path, valueOld, valueNew});
			return true;
		}
		// Both are mergeable objects (Array or plain Object)
		if (typeof valueNew === 'object' && valueNew && typeof valueOld === 'object' && valueOld) {
			if (markSeen(valueNew, valueOld)) {
				// Cycle already visited => consider unchanged at this node to avoid infinite recursion
				if (path) {
					rootDiff.unchanged.push(path);
				}
				return false;
			}
		}
		let changed = false;
		if (valueNew instanceof Array && valueOld instanceof Array) {
			const max = Math.max(valueNew.length, valueOld.length);
			for (let i = 0; i < max; ++i) {
				const p = joinPath(path, String(i));
				changed = walk(valueNew[i], valueOld[i], p) || changed;
			}
		} else {
			const keys = new Set<string>([
				...Object.keys(valueNew),
				...Object.keys(valueOld)
			]);
			for (const key of keys) {
				const p = joinPath(path, key);
				changed = walk(valueNew[key], valueOld[key], p) || changed;
			}
		}
		// If no nested change, mark this node itself as unchanged (except root)
		if (!changed && path) {
			rootDiff.unchanged.push(path);
		}
		return changed;
	};
	walk(_new, _old, '');
	return rootDiff;
};

export function setTakeOne<T>(
	set: Set<T>
) {
	if (set.size === 0) {
		return (undefined);
	}
	const item = <T>set.values().next().value;
	set.delete(item);
	return (item);
};

export function fromKeyValueList<T extends readonly (readonly [string, unknown])[]>(
	from: T
): {
	[K in T[number] as K[0]]: K[1];
} {
	return Object.fromEntries(from) as {
		[K in T[number] as K[0]]: K[1];
	};
};

const internalHasSameContent = (isDeepCheck: boolean, LHS: object, RHS: object): boolean => {
	const entriesLHS = Object.entries(LHS);
	const entriesRHS = Object.entries(RHS);
	const self = internalHasSameContent.bind(null, isDeepCheck);
	type IU = Iterable<unknown>;
	const selfAF = (x: IU, y: IU) => self(Array.from(x), Array.from(y));
	return (
		(entriesLHS.length !== entriesRHS.length) ? false :
			((LHS instanceof Set) || (LHS instanceof Map)) ? self([LHS], [RHS]) :
				(
					entriesLHS.map(x => [x[1], (entriesRHS.find(y => y[0] === x[0]) || [])[1]]).
					filter(xy => {
						const [x, y, tx] = [...xy, typeof xy[0]];
						return (
							((!x && y) || (x && !y)) ? true :
								(!x && !y) ? false :
									(tx !== typeof y) ? true :
										(tx !== 'object') ? (x !== y) :
											(x.constructor !== y.constructor) ? true :
												!isDeepCheck ? (x !== y) : (
													(x instanceof Set) ? !selfAF(x.values(), y.values()) :
														(x instanceof Map) ? !selfAF(x.entries(), y.entries()) :
															!self(x, y)
												)
						);
					}).length
				) === 0
	);
};
export const hasSameContent = internalHasSameContent.bind(null, false);
export const hasSameContentDeeply = internalHasSameContent.bind(null, true);

export function copyWith<T extends object, TK extends keyof T>(
	object: T,
	withProperties: TK[]
): Pick<T, TK> {
	return <Pick<T, TK>>(fromKeyValueList(
		Object.entries(object).filter(([k]) => (withProperties as string[]).includes(k))
	));
};

export function copyWithout<T extends object, TK extends keyof T>(
	object: T,
	withoutProperties: TK[]
): Omit<T, TK> {
	return <Omit<T, TK>>(fromKeyValueList(
		Object.entries(object).
		filter(([k]) => !withoutProperties.includes(<TK>k))
	));
};

export function visit(host: object, fn: (container: object, path: string) => void) {
	const f = (container: object, path: string[]) => {
		fn(container, path.join('.'));
		for (const [k, v] of Object.entries(container)) {
			if (v && (typeof v === 'object')) {
				f(v, [...path, k]);
			}
		}
	};
	f(host, []);
};

/**
 * Like native "Object.entries" but with a correct types enumeration.
 **/
export function entries<T extends object>(source: T): [keyof T & string, T[keyof T]][] {
	return Object.entries(source) as [keyof T & string, T[keyof T]][];
};

/**
 * Like native "Object.values" but with a correct types enumeration.
 **/
export function values<T extends object>(source: T): (keyof T extends string ? T[keyof T] : never)[] {
	return Object.values(source);
};
/* JSON types are not goodly working! */
export type JSONScalar = (
	string |
	boolean |
	number |
	null
	);
/* JSON types are not goodly working! */
export type JSONScalarArray = JSONScalar[];
/* JSON types are not goodly working! */
export type JSONScalarRecord = Record<string | number, JSONScalar>;
/* JSON types are not goodly working! */
export type JSONComplex = (
	JSONScalarArray |
	JSONScalarArray[] |
	JSONScalarRecord |
	JSONScalarRecord[]
	);
/* JSON types are not goodly working! */
export type JSONComplexRecursive = (
	JSONComplex[] |
	Record<
		string | number,
		JSONComplex | JSONComplex[] |
		Record<
			string | number,
			JSONComplex | JSONComplex[]
		>
	>
	);
/* JSON types are not goodly working! */
export type JSON = (
	JSONScalar |
	JSONScalarArray |
	JSONScalarRecord |
	JSONComplex |
	JSONComplexRecursive
	);

export type SetWritable<
	T extends object,
	K extends keyof T = keyof T
> = {
	-readonly [P in K]: T[P];
} & {
	[P in Exclude<keyof T, K>]: T[P];
};

export type SetWritableAll<
	T extends object,
	TAlsoNullable extends boolean = false
> = {
	-readonly [P in keyof T]: TAlsoNullable extends true ? T[P] | null : T[P];
};

export function emptyAllReferences<T extends object>(target: T) {
	const o = <SetWritableAll<T, true>>target;
	for (const [k, v] of entries(o)) {
		if (typeof v === 'object' && v) {
			o[k] = null;
		}
	}
};

/**
 * Check that RHS satisfies LHS.
 **/
export type AssertSatisfies<
	TExpected,
	TActual extends TExpected
> = TActual;


export namespace JSON {
	export function parse(...parameters: Parameters<typeof globalThis.JSON.parse>): any {
		const [source, ...others] = parameters;
		try {
			return globalThis.JSON.parse(source, ...others);
		} catch (error: unknown) {
			try {
				return globalThis.JSON.parse(jsonrepair(source), ...others);
			} catch (error2: unknown) {
				void error2;
				throw error;
			}
		}
	};

	export function stringify(...parameters: Parameters<typeof globalThis.JSON.stringify>): any {
		const [object, ...others] = parameters;
		return globalThis.JSON.stringify(object, ...others);
	};
}

export type ArrayShift<T extends any[]> = T extends [any, ...infer Rest] ? Rest : never;

export type TypedArray =
	| Float32Array | Float64Array
	| Int8Array | Int16Array | Int32Array
	| Uint8Array | Uint8ClampedArray | Uint16Array | Uint32Array
	| BigInt64Array | BigUint64Array;

export type FixedArray<TType, TLength> = (TType extends TypedArray ? TType : TType[]) & {
	readonly length: TLength;
};

export function arrayCountOccurrences<T>(subject: T[]) {
	return subject.reduce<Map<T, number>>((a, c) => {
		const occurrenceCount = a.get(c);
		if (!occurrenceCount) {
			a.set(c, 1);
		} else {
			a.set(c, occurrenceCount + 1);
		}
		return a;
	}, new Map);
};

export function has<
	Obj extends object,
	Prop extends PropertyKey,
	T extends 'object' | 'function' | 'string' | 'number' | 'boolean' | 'undefined'
>(
	context: Obj,
	property: Prop,
	type: T
): context is Obj & {
	[K in Prop]: (
		T extends 'object' ? object :
			T extends 'function' ? Function :
				T extends 'string' ? string :
					T extends 'number' ? number :
						T extends 'boolean' ? boolean :
							undefined
		)
} {
	return property in context && typeof (context as any)[property] === type;
}

/**
 * Call an async function on each element of an array and return the result as a new array.
 * Each call awaits that the previous one is resolved.
 **/
export async function asyncMapOneByOne<TIn, TOut>(
	elements: TIn[],
	fn: (element: TIn, index: number) => Promise<TOut>
) {
	const imax = elements.length;
	const out = new Array<TOut>(imax);
	for (let i = 0; i < imax; ++i) {
		out[i] = await fn(elements[i]!, i);
	}
	return out;
};

/**
 * Filter an array in place and return the filtered elements.
 **/
export function arrayInPlaceFilter<T>(
	array: T[],
	predicate: (element: T, index: number) => boolean
) {
	let i = 0;
	const filtered = [];
	while (i < array.length) {
		const v = array[i]!;
		if (!predicate(v, i)) {
			array.splice(i, 1);
			filtered.push(v);
		} else {
			++i;
		}
	}
	return filtered;
};
